import AnalyticsIcons from '@/icons/AnalyticsIcons'
import CheckIcons from '@/icons/CheckIcons'
import PackageIcons from '@/icons/PackageIcons'
import ReleaseNoteIcons from '@/icons/ReleaseNoteIcons'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorIcons, Typogaphy, WarningIcons } from 'vnetwork-ui'
import { GeminiReportResponse } from '../../services/gemini'

interface AIAnalysisReportProps {
    report: GeminiReportResponse | null
    loading: boolean
    error?: string | null
    fromCache?: boolean
}

const AIAnalysisReport: React.FC<AIAnalysisReportProps> = ({
    report,
    loading,
    error,
    fromCache = false,
}) => {
    const { t } = useTranslation()

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-green-600'
        if (score >= 60) return 'text-yellow-600'
        return 'text-red-600'
    }

    const getScoreBg = (score: number) => {
        if (score >= 80) return 'bg-green-100'
        if (score >= 60) return 'bg-yellow-100'
        return 'bg-red-100'
    }

    // Don't render if no data to show
    if (!loading && !error && !report) {
        return null
    }

    return (
        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-2">
            <Typogaphy className="text-xl font-bold flex items-center gap-2 text-primary">
                <AnalyticsIcons className="w-5 h-5" /> {t('ai_analysis_report')}
            </Typogaphy>

            <div className="space-y-2">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">
                                {t('ai_analyzing')}...
                            </p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-red-800 font-medium">
                            <ErrorIcons className="w-5 h-5 text-c-error" />{' '}
                            {t('analysis_error')}
                        </p>
                        <p className="text-red-600 text-sm mt-1">{error}</p>
                    </div>
                )}

                {report && !loading && (
                    <>
                        {/* Financial Health Score */}
                        <div
                            className={`${getScoreBg(report.financial_health_score)} rounded-md p-6 border-2 border-gray-200`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                                        {t('financial_health_score')}
                                    </h3>
                                    <p
                                        className={`text-5xl font-bold ${getScoreColor(report.financial_health_score)}`}
                                    >
                                        {report.financial_health_score}
                                        <span className="text-2xl">/100</span>
                                    </p>
                                </div>
                                <div className="text-6xl">
                                    {report.financial_health_score >= 80 ? (
                                        '🎯'
                                    ) : report.financial_health_score >= 60 ? (
                                        <WarningIcons className="w-10 h-10 text-warning" />
                                    ) : (
                                        <ErrorIcons className="w-10 h-10 text-error" />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Executive Summary */}
                        <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
                            <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                <ReleaseNoteIcons className="w-5 h-5" />{' '}
                                {t('executive_summary')}
                            </h3>
                            <p className="text-gray-800 leading-relaxed">
                                {report.executive_summary}
                            </p>
                        </div>

                        {/* Key Insights */}
                        <div className="bg-white rounded-md p-4 border border-gray-200">
                            <Typogaphy className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span>💡</span> {t('key_insights')}
                            </Typogaphy>
                            <ul className="space-y-2">
                                {report?.key_insights?.map((insight, idx) => (
                                    <li
                                        key={idx}
                                        className="flex items-center gap-2"
                                    >
                                        <span className="text-blue-600 font-bold mt-0.5">
                                            •
                                        </span>
                                        <span className="text-gray-700">
                                            {insight}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Trends Analysis */}
                        <div className="bg-white rounded-md p-4 border border-gray-200">
                            <Typogaphy className="text-sm font-medium text-c-success mb-2 flex items-center gap-2">
                                <TrendingUpIcons className="w-5 h-5 text-c-success" />{' '}
                                {t('trends_analysis')}
                            </Typogaphy>
                            <Typogaphy className="text-gray-700 leading-relaxed whitespace-pre-line">
                                {report.trends_analysis}
                            </Typogaphy>
                        </div>

                        {/* Risk Alerts */}
                        {report?.risk_alerts?.length > 0 && (
                            <div className="bg-red-50 rounded-md p-4 border border-red-200">
                                <Typogaphy className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2">
                                    <WarningIcons className="w-5 h-5" />{' '}
                                    {t('risk_alerts')}
                                </Typogaphy>
                                <ul className="space-y-2">
                                    {report.risk_alerts.map((risk, idx) => (
                                        <li
                                            key={idx}
                                            className="flex gap-2 items-center"
                                        >
                                            <WarningIcons className="w-3 h-3 text-c-error" />
                                            <Typogaphy className="text-c-error">
                                                {risk}
                                            </Typogaphy>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Recommendations */}
                        <div className="bg-green-50 rounded-md p-4 border border-green-200">
                            <Typogaphy className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                                <CheckIcons className="w-5 h-5" />{' '}
                                {t('recommendations')}
                            </Typogaphy>
                            <ul className="space-y-3">
                                {report?.recommendations?.map(
                                    (recommendation, idx) => (
                                        <li
                                            key={idx}
                                            className="flex items-start gap-2"
                                        >
                                            <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                                {idx + 1}
                                            </span>
                                            <span className="text-gray-800">
                                                {recommendation}
                                            </span>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>

                        {/* Footer */}
                        <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-end gap-2">
                                <span>
                                    🤖 {t('powered_by_gemini_ai')} •{' '}
                                    {new Date().toLocaleString('vi-VN')}
                                </span>
                                {fromCache && (
                                    <Typogaphy className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                        <PackageIcons className="w-4 h-4" />{' '}
                                        Cached
                                    </Typogaphy>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default AIAnalysisReport
